import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/primitives";
import { ReadingHero, LongFormBody } from "@/components/site/LongForm";
import { POSTS } from "@/data/blog";

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const idx = POSTS.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/blog" replace />;
  const p = POSTS[idx];
  const next = POSTS[(idx + 1) % POSTS.length];

  const bibtex = `@techreport{datafrontier_${p.id.replace("-", "").toLowerCase()},
  title  = {${p.title}},
  author = {{DataFrontier Research}},
  year   = {${new Date(p.date).getFullYear()}},
  number = {${p.id}},
  institution = {DataFrontier Innovations Private Limited},
  url    = {https://datafrontier.co/blog/${p.slug}}
}`;

  return (
    <div data-testid="blog-post-page">
      <Seo title={p.title} description={p.abstract} />
      <ReadingHero
        eyebrow={`${p.id} · Lab note`}
        title={p.title}
        meta={
          <>
            <span>{fmt(p.date)}</span>
            <span className="opacity-40">·</span>
            <span>{p.readTime} read</span>
            <span className="opacity-40">·</span>
            <span>DataFrontier Research</span>
          </>
        }
        abstract={p.abstract}
      />
      <LongFormBody content={p.content} />

      {/* Cite this note */}
      <div className="border-t border-ink/10 bg-bone">
        <div className="mx-auto max-w-[720px] px-5 pb-16 md:px-8">
          <div className="font-mono text-xs uppercase tracking-[0.16em] text-ink/50">Cite this note</div>
          <pre className="mt-4 overflow-x-auto border border-ink/15 bg-white p-5 font-mono text-[12px] leading-relaxed text-ink/80">
            <code>{bibtex}</code>
          </pre>
        </div>
      </div>

      {/* Prev / next */}
      <div className="border-t border-ink/10 bg-bone">
        <div className="mx-auto flex max-w-[720px] items-center justify-between px-5 py-10 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-xs text-ink/60 hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> All notes
          </Link>
          <Link to={`/blog/${next.slug}`} className="group inline-flex items-center gap-3 text-right">
            <span>
              <span className="block font-mono text-xs text-ink/50">{next.id} · Next</span>
              <span className="mt-1 block max-w-xs font-serif text-lg leading-snug text-ink group-hover:text-saffron">
                {next.title}
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-ink/60 group-hover:text-saffron" />
          </Link>
        </div>
      </div>
    </div>
  );
}
